import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

/* Payroll / Accounts — payroll tab uses live HR API */

const inr = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const SALARY_MAP = { doctor: 120000, nurse: 28000, billing: 22000, reception: 20000, pharmacy: 25000, lab: 26000, manager: 80000, hr: 35000, ipd: 22000, housekeeping: 18000 };

const buildLine = (s) => {
  const base = Number(s.ctc) || SALARY_MAP[s.role] || 20000;
  const hra = Math.round(base * 0.4);
  const da = Math.round(base * 0.1);
  const ta = Math.round(base * 0.05);
  const special = s.role === 'doctor' ? 15000 : 0;
  const pf = Math.round(base * 0.12);
  const esi = base < 21000 ? Math.round(base * 0.0075) : 0;
  const pt = 200;
  const tds = s.role === 'doctor' ? Math.round(base * 0.1) : 0;
  const loans = 0;
  const absent = 0;
  const gross = base + hra + da + ta + special;
  const absentDeduction = Math.round((base / 26) * absent);
  const totalDeductions = pf + esi + pt + tds + loans + absentDeduction;
  const net = gross - totalDeductions;
  return {
    id: s.staffId || s.uid || s.email,
    staffId: s.staffId || s.uid,
    name: s.name,
    dept: s.roleLabel || s.role,
    designation: s.designation || s.role,
    role: s.role,
    basic: base, hra, ta, da, special, pf, esi, pt, tds, loans, absent,
    gross, absentDeduction, totalDeductions, net,
  };
};

function PayrollTab() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [processed, setProcessed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const { data: staffData } = useLiveData(() => ipApi.getStaff(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = (staffData?.staff || []).filter((s) => s.active !== false);
      try {
        const data = await ipApi.getHrPayroll(month);
        if (data?.payroll?.lines?.length && !cancelled) {
          setStaff(data.payroll.lines.map((l) => ({
            id: l.staffId,
            staffId: l.staffId,
            name: l.name,
            dept: l.role,
            designation: l.role,
            role: l.role,
            basic: l.base,
            hra: l.hra,
            da: l.da,
            ta: 0,
            special: 0,
            pf: l.pf,
            esi: 0,
            pt: 0,
            tds: l.tds,
            loans: 0,
            absent: 0,
            gross: l.base + l.hra + l.da,
            totalDeductions: l.pf + l.tds,
            net: l.net,
          })));
          setProcessed(true);
        } else if (!cancelled) {
          setStaff(list.map(buildLine));
          setProcessed(false);
        }
      } catch {
        if (!cancelled) {
          setStaff(list.map(buildLine));
          setProcessed(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [staffData, month]);

  const processPayroll = async () => {
    setBusy(true);
    setMsg('');
    try {
      const lines = staff.map((e) => ({
        staffId: e.staffId || e.id,
        name: e.name,
        role: e.role || e.dept,
        base: e.basic,
        hra: e.hra,
        da: e.da,
        pf: e.pf,
        tds: e.tds,
        net: e.net,
      }));
      await ipApi.saveHrPayroll({ month, lines });
      setProcessed(true);
      setMsg(`Payroll processed for ${month}.`);
    } catch (e) {
      setMsg(e.message || 'Process failed');
    } finally {
      setBusy(false);
    }
  };

  const printSlip = (emp) => {
    const win = window.open('', '_blank', 'width=700,height=800');
    if (!win) return;
    win.document.write(`<html><head><title>Payslip</title><style>
      body{font-family:Arial,sans-serif;max-width:650px;margin:20px auto;font-size:13px}
      .header{text-align:center;border-bottom:2px solid #0d6e6a;padding-bottom:10px;margin-bottom:15px}
      table{width:100%;border-collapse:collapse;margin:10px 0}
      td,th{padding:7px 10px;border:1px solid #e2e8f0}th{background:#f1f5f9;font-weight:700}
      .total{font-weight:700;background:#f8fafc}.net{font-size:16px;font-weight:900;color:#0d6e6a}
    </style></head><body>
      <div class="header"><h2>PAYSLIP — ${month}</h2><p>Confidential — Not for distribution</p></div>
      <table><tr><td><strong>Name:</strong> ${emp.name}</td><td><strong>Emp ID:</strong> ${emp.id}</td></tr>
      <tr><td><strong>Department:</strong> ${emp.dept}</td><td><strong>Designation:</strong> ${emp.designation}</td></tr>
      <tr><td><strong>Month:</strong> ${month}</td><td><strong>Days Absent:</strong> ${emp.absent}</td></tr></table>
      <table><tr><th>Earnings</th><th>Amount (₹)</th><th>Deductions</th><th>Amount (₹)</th></tr>
      <tr><td>Basic Salary</td><td>${emp.basic.toLocaleString('en-IN')}</td><td>Provident Fund (PF)</td><td>${emp.pf.toLocaleString('en-IN')}</td></tr>
      <tr><td>HRA</td><td>${emp.hra.toLocaleString('en-IN')}</td><td>ESI</td><td>${emp.esi.toLocaleString('en-IN')}</td></tr>
      <tr><td>Travel Allowance</td><td>${emp.ta.toLocaleString('en-IN')}</td><td>Professional Tax</td><td>${emp.pt.toLocaleString('en-IN')}</td></tr>
      <tr><td>DA</td><td>${emp.da.toLocaleString('en-IN')}</td><td>TDS / Income Tax</td><td>${emp.tds.toLocaleString('en-IN')}</td></tr>
      <tr><td>Special Allowance</td><td>${emp.special.toLocaleString('en-IN')}</td><td>Loan Deduction</td><td>${emp.loans.toLocaleString('en-IN')}</td></tr>
      <tr><td></td><td></td><td>Absent Deduction</td><td>${(emp.absentDeduction || 0).toLocaleString('en-IN')}</td></tr>
      <tr class="total"><td>Gross Salary</td><td>${(emp.gross || 0).toLocaleString('en-IN')}</td><td>Total Deductions</td><td>${(emp.totalDeductions || 0).toLocaleString('en-IN')}</td></tr>
      </table><div style="text-align:center;padding:15px 0"><span class="net">NET SALARY: ₹${(emp.net || 0).toLocaleString('en-IN')}</span></div>
      <p style="text-align:center;font-size:11px;color:#94a3b8">This is a system-generated payslip. No signature required.</p>
    </body></html>`);
    win.document.close(); win.print();
  };

  const totalGross = staff.reduce((s, e) => s + (e.gross || 0), 0);
  const totalNet = staff.reduce((s, e) => s + (e.net || 0), 0);
  const totalPf = staff.reduce((s, e) => s + (e.pf || 0), 0);
  const totalEsi = staff.reduce((s, e) => s + (e.esi || 0), 0);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Total Staff', val: staff.length, color: '#6366f1' }, { label: 'Gross Payroll', val: inr(totalGross), color: '#3b82f6' }, { label: 'Net Payable', val: inr(totalNet), color: '#10b981' }, { label: 'PF Contribution', val: inr(totalPf * 2), color: '#f59e0b' }, { label: 'ESI Contribution', val: inr(totalEsi * 4.75), color: '#8b5cf6' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 16, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Month</label><input type="month" className="input" value={month} onChange={e => setMonth(e.target.value)} /></div>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={processPayroll}>{busy ? 'Saving…' : processed ? 'Re-process payroll' : 'Process payroll'}</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => staff.forEach(printSlip)}>🖨 Print all payslips</button>
      </div>
      {msg && <div className="info-banner mb">{msg}</div>}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Salary Register — {month}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12 }}>
            <thead>
              <tr><th>Emp ID</th><th>Name</th><th>Dept</th><th>Designation</th><th>Gross (₹)</th><th>Deductions (₹)</th><th>Net Payable (₹)</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {staff.map(emp => (
                  <tr key={emp.id}>
                    <td style={{ fontFamily: 'monospace', color: '#6366f1', fontWeight: 700 }}>{emp.id}</td>
                    <td style={{ fontWeight: 700 }}>{emp.name}</td>
                    <td>{emp.dept}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{emp.designation}</td>
                    <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#3b82f6' }}>{(emp.gross || 0).toLocaleString('en-IN')}</td>
                    <td style={{ fontWeight: 700, fontFamily: 'Outfit,sans-serif', color: '#f97316' }}>{(emp.totalDeductions || 0).toLocaleString('en-IN')}</td>
                    <td style={{ fontWeight: 900, fontFamily: 'Outfit,sans-serif', color: '#10b981', fontSize: 14 }}>{(emp.net || 0).toLocaleString('en-IN')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" className="btn btn-sm btn-primary" style={{ fontSize: 10 }} onClick={() => printSlip(emp)}>🖨 Payslip</button>
                        <button type="button" className="btn btn-sm btn-secondary" style={{ fontSize: 10 }} onClick={() => setSelected(selected?.id === emp.id ? null : emp)}>Edit</button>
                      </div>
                    </td>
                  </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No staff — add logins under Staff &amp; Logins</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AccountsTab() {
  const { data: expData, reload } = useLiveData(() => ipApi.getExpenses(80), []);
  const { data: stats, loading: statsLoading } = useLiveData(() => ipApi.getAnalytics({ period: 'month' }), []);
  const expenses = (expData?.expenses || []).map((e) => ({
    id: e.expenseId,
    date: e.date,
    category: e.category,
    description: e.description,
    amount: e.amount,
    mode: e.paidBy || e.vendor || 'Cash',
    by: e.createdBy || 'Accounts',
  }));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: 'Utilities', description: '', amount: '', paidBy: 'Bank Transfer' });
  const [saving, setSaving] = useState(false);

  const addExpense = async () => {
    if (!form.description || !form.amount) return;
    setSaving(true);
    try {
      await ipApi.createExpense({
        description: form.description,
        category: form.category,
        amount: Number(form.amount),
        paidBy: form.paidBy,
        date: new Date().toISOString().slice(0, 10),
      });
      setShowForm(false);
      setForm({ category: 'Utilities', description: '', amount: '', paidBy: 'Bank Transfer' });
      reload();
    } finally {
      setSaving(false);
    }
  };

  const REVENUE = [
    { category: 'OP Collections (this month)', amount: Number(stats?.opReceiptsTotal || 0), pct: 0 },
    { category: 'All receipts (this month)', amount: Number(stats?.receiptsTotal || 0), pct: 0 },
    { category: 'IP outstanding (live)', amount: Number(stats?.ipOutstanding || 0), pct: 0 },
  ];
  const totalRevenue = Number(stats?.receiptsTotal || 0);
  const totalExpense = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const profit = totalRevenue - totalExpense;
  const revenueWithPct = REVENUE.map((r) => {
    const pct = totalRevenue > 0 ? Math.round((r.amount / totalRevenue) * 100) : 0;
    return { ...r, pct };
  });

  return (
    <div>
      <div className="info-banner mb">
        P&amp;L uses live receipts and expenses logged in HCEN. Add expenses below; revenue comes from billing analytics.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Revenue (month)', val: statsLoading ? '…' : inr(totalRevenue), color: '#10b981' }, { label: 'Expenses logged', val: inr(totalExpense), color: '#f97316' }, { label: 'Net Profit/Loss', val: statsLoading ? '…' : inr(profit), color: profit > 0 ? '#10b981' : '#dc2626' }, { label: 'Profit Margin', val: totalRevenue > 0 ? `${Math.round((profit / totalRevenue) * 100)}%` : '—', color: profit > 0 ? '#10b981' : '#dc2626' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 14, textAlign: 'center', borderTop: `4px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#10b981' }}>📈 Revenue (live)</h3>
          {revenueWithPct.map(r => (
            <div key={r.category} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.category}</span>
                <span style={{ fontWeight: 800, fontFamily: 'Outfit,sans-serif', color: '#10b981' }}>{inr(r.amount)}</span>
              </div>
              <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                <div style={{ width: `${r.pct}%`, background: '#10b981', height: 6, borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>💸 Expense Ledger</h3>
          <div className="table-wrap">
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th><th>Mode</th></tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td style={{ fontWeight: 600, fontSize: 11 }}>{e.category}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{e.description}</td>
                    <td style={{ fontWeight: 700, color: '#f97316', fontFamily: 'Outfit,sans-serif' }}>{inr(e.amount)}</td>
                    <td style={{ fontSize: 11 }}>{e.mode}</td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No expenses logged yet</td></tr>}
              </tbody>
            </table>
          </div>
          {showForm && (
            <div className="form-grid mb" style={{ marginTop: 10 }}>
              <div className="field"><label>Category</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="field"><label>Description</label><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="field"><label>Amount</label><input type="number" className="input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="field"><label>Payment mode</label><input className="input" value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} /></div>
              <div className="form-actions full">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={addExpense}>{saving ? 'Saving…' : 'Save expense'}</button>
              </div>
            </div>
          )}
          <button type="button" className="btn btn-secondary btn-sm mt" style={{ marginTop: 10 }} onClick={() => setShowForm((s) => !s)}>+ Add Expense</button>
        </div>
      </div>
    </div>
  );
}

export default function PayrollPage() {
  const [tab, setTab] = useState('payroll');
  return (
    <PageShell title="Payroll & Accounts" subtitle="Salary Processing · PF/ESI/TDS · Payslips · Revenue vs Expense · P&L"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>📊 Monthly Report</button>}>
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'payroll', label: '💰 Payroll Processing' }, { id: 'accounts', label: '📊 Accounts & P&L' }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'payroll' && <PayrollTab />}
      {tab === 'accounts' && <AccountsTab />}
    </PageShell>
  );
}
